import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Select } from "../shared/Select";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { useLiveTrades } from "../../hooks/api/trades/useLiveTrades";
import { ILiveTrade, TradeInstrumentType } from "../../interfaces/liveTrade";
import {
  FilterGroup,
  FilterInput,
  FiltersWrapper,
  HeaderRow,
  LastUpdatedText,
  LiveTradesWrapper,
  PageTitle,
  TableContainer,
  WarningText,
} from "./style";
import { LiveTradesTable } from "./LiveTradesTable";
import { formatAmount } from "../../utils/strings";
import { DataContext } from "../../context/DataContext";

type TradeCategory = "PERPETUAL" | "OPTION" | "LIQUIDATION";

const TRADE_CATEGORIES: { label: string; value: TradeCategory }[] = [
  { label: "Perps", value: "PERPETUAL" },
  { label: "Options", value: "OPTION" },
  { label: "Liquidations", value: "LIQUIDATION" },
];

const MIN_NOTIONAL_FILTERS = [5000, 10000, 50000, 100000];
const DEFAULT_CATEGORY_SELECTION: TradeCategory[] = [
  "PERPETUAL",
  "OPTION",
  "LIQUIDATION",
];
const DEFAULT_THRESHOLD: number | null = null;
const DEFAULT_LOOKBACK_MINUTES = 180; // 3 hours

const computeNotional = (trade: ILiveTrade) => {
  if (trade.notional !== undefined) {
    const notionalValue = Number(trade.notional);
    if (!Number.isNaN(notionalValue)) {
      return notionalValue;
    }
  }

  const amount = Number(trade.amount);
  const price = Number(trade.avg_price);

  if (Number.isNaN(amount) || Number.isNaN(price)) {
    return 0;
  }

  return amount * price;
};

export function LiveTrades() {
  const [selectedCategories, setSelectedCategories] = useState<TradeCategory[]>(
    DEFAULT_CATEGORY_SELECTION
  );
  const [minNotional, setMinNotional] = useState<number | null>(DEFAULT_THRESHOLD);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<string>("");
  const [takerAddressFilter, setTakerAddressFilter] = useState<string>("");
  const [makerAddressFilter, setMakerAddressFilter] = useState<string>("");
  const [lookbackMinutes, setLookbackMinutes] = useState<number>(DEFAULT_LOOKBACK_MINUTES);
  const [lookbackInput, setLookbackInput] = useState<string>(DEFAULT_LOOKBACK_MINUTES.toString());
  const { records } = useContext(DataContext);
  const addressLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    if (records) {
      Object.entries(records).forEach(([address, label]) => {
        if (!address || !label) {
          return;
        }
        labels[address.toLowerCase()] = label;
      });
    }
    return labels;
  }, [records]);

  const includeLiquidations = selectedCategories.includes("LIQUIDATION");
  const instrumentTypes = useMemo<TradeInstrumentType[]>(
    () =>
      selectedCategories.filter(
        (category): category is TradeInstrumentType =>
          category === "PERPETUAL" || category === "OPTION"
      ),
    [selectedCategories]
  );

  const {
    data: tradesData,
    error,
    isValidating,
  } = useLiveTrades({
    instrumentTypes,
    minNotional: minNotional ?? undefined,
    includeLiquidations,
    asset: selectedAsset.trim().toUpperCase() || undefined,
    lookbackMinutes,
  });

  useEffect(() => {
    if (tradesData !== undefined) {
      setLastUpdated(Date.now());
    }
  }, [tradesData]);

  const isLoading = !tradesData && isValidating;

  const toggleCategory = useCallback(
    (category: TradeCategory) => {
      setSelectedCategories((prev) => {
        const exists = prev.includes(category);
        if (exists) {
          if (prev.length === 1) {
            return prev;
          }
          return prev.filter((type) => type !== category);
        }
        return [...prev, category];
      });
    },
    [setSelectedCategories]
  );

  const resetFilters = useCallback(() => {
    setSelectedCategories([...DEFAULT_CATEGORY_SELECTION]);
    setMinNotional(DEFAULT_THRESHOLD);
    setSelectedAsset("");
    setTakerAddressFilter("");
    setMakerAddressFilter("");
    setLookbackMinutes(DEFAULT_LOOKBACK_MINUTES);
    setLookbackInput(DEFAULT_LOOKBACK_MINUTES.toString());
  }, []);

  const tradeAsset = useCallback((trade: ILiveTrade) => {
    if (trade.asset) {
      return trade.asset.toUpperCase();
    }

    if (trade.instrument_name) {
      const [asset] = trade.instrument_name.split("-");
      if (asset) {
        return asset.toUpperCase();
      }
    }

    return "UNKNOWN";
  }, []);

  const filteredTrades = useMemo(() => {
    if (!tradesData) {
      return [];
    }

    const includePerps = selectedCategories.includes("PERPETUAL");
    const includeOptions = selectedCategories.includes("OPTION");

    const normalizedTakerFilter = takerAddressFilter.trim().toLowerCase();
    const normalizedMakerFilter = makerAddressFilter.trim().toLowerCase();
    const normalizedAsset = selectedAsset.trim().toUpperCase();

    return tradesData.filter((trade) => {
      const notional = computeNotional(trade);
      if (minNotional !== null && notional < minNotional) {
        return false;
      }

      if (normalizedAsset && tradeAsset(trade) !== normalizedAsset) {
        return false;
      }

      const isLiquidation = Boolean(trade.liquidation || trade.is_liquidation);

      if (isLiquidation && !includeLiquidations) {
        return false;
      }

      const matchesInstrument =
        (trade.instrument_type === "PERPETUAL" && includePerps) ||
        (trade.instrument_type === "OPTION" && includeOptions);

      if (includeLiquidations && !includePerps && !includeOptions) {
        return isLiquidation;
      }

      const takerAddressRaw = (trade.taker ?? trade.taker_account ?? "").toLowerCase();
      if (normalizedTakerFilter) {
        const takerLabel = addressLabels[takerAddressRaw] ?? "";
        const matchesAddress = takerAddressRaw.includes(normalizedTakerFilter);
        const matchesLabel = takerLabel.toLowerCase().includes(normalizedTakerFilter);
        if (!matchesAddress && !matchesLabel) {
          return false;
        }
      }

      if (normalizedMakerFilter) {
        const makers = Array.isArray(trade.maker) ? trade.maker : [];
        const makerMatches = makers.some((makerAddress) => {
          const makerAddressRaw = makerAddress.toLowerCase();
          const makerLabel = addressLabels[makerAddressRaw] ?? "";
          const matchesAddress = makerAddressRaw.includes(normalizedMakerFilter);
          const matchesLabel = makerLabel.toLowerCase().includes(normalizedMakerFilter);
          return matchesAddress || matchesLabel;
        });
        if (!makerMatches) {
          return false;
        }
      }

      return matchesInstrument;
    });
  }, [
    addressLabels,
    includeLiquidations,
    makerAddressFilter,
    minNotional,
    selectedAsset,
    selectedCategories,
    takerAddressFilter,
    tradeAsset,
    tradesData,
  ]);

  const categoryOptions = useMemo(
    () =>
      TRADE_CATEGORIES.map(({ label, value }) => ({
        label,
        isActive: selectedCategories.includes(value),
        onClick: () => toggleCategory(value),
      })),
    [selectedCategories, toggleCategory]
  );

  const thresholdOptions = useMemo(
    () =>
      [
        { label: "No Min", value: null as number | null },
        ...MIN_NOTIONAL_FILTERS.map((threshold) => ({
          label: `>$${formatAmount(threshold)}`,
          value: threshold,
        })),
      ].map(({ label, value }) => ({
        label,
        isActive:
          value === null
            ? minNotional === null
            : minNotional === value,
        onClick: () => setMinNotional(value),
      })),
    [minNotional]
  );

  return (
    <LiveTradesWrapper>
      <HeaderRow>
        <PageTitle>Live Trades Monitor</PageTitle>
        <div>
          {lastUpdated ? (
            <LastUpdatedText>
              Last updated {moment(lastUpdated).format("HH:mm:ss")} • Streams
              refresh every 5s
            </LastUpdatedText>
          ) : (
            <LastUpdatedText>Waiting for first update…</LastUpdatedText>
          )}
        </div>
      </HeaderRow>
      <FiltersWrapper>
        <FilterGroup>
          <span>Categories</span>
          <Select options={categoryOptions} />
        </FilterGroup>
        <FilterGroup>
          <span>Min Notional</span>
          <Select options={thresholdOptions} />
        </FilterGroup>
        <FilterGroup>
          <span>Asset</span>
          <FilterInput
            value={selectedAsset}
            placeholder="Leave blank for all"
            onChange={(event) =>
              setSelectedAsset(event.target.value.toUpperCase())
            }
          />
        </FilterGroup>
        <FilterGroup>
          <span>Lookback (minutes)</span>
          <FilterInput
            type="number"
            value={lookbackInput}
            placeholder="180"
            min="1"
            onChange={(event) => {
              const inputValue = event.target.value;
              setLookbackInput(inputValue);
              const value = parseInt(inputValue, 10);
              if (!Number.isNaN(value) && value > 0) {
                setLookbackMinutes(value);
              }
            }}
            onBlur={(event) => {
              const inputValue = event.target.value;
              const value = parseInt(inputValue, 10);
              if (inputValue === "" || Number.isNaN(value) || value <= 0) {
                setLookbackMinutes(DEFAULT_LOOKBACK_MINUTES);
                setLookbackInput(DEFAULT_LOOKBACK_MINUTES.toString());
              } else {
                setLookbackInput(value.toString());
              }
            }}
            style={{ minWidth: "100px" }}
          />
        </FilterGroup>
        <FilterGroup>
          <span>Taker</span>
          <FilterInput
            value={takerAddressFilter}
            placeholder="Filter address or label"
            onChange={(event) => setTakerAddressFilter(event.target.value)}
          />
        </FilterGroup>
        <FilterGroup>
          <span>Maker</span>
          <FilterInput
            value={makerAddressFilter}
            placeholder="Filter address or label"
            onChange={(event) => setMakerAddressFilter(event.target.value)}
          />
        </FilterGroup>
        <FilterGroup>
          <Button buttonTheme={ButtonThemeEnum.NEUTRAL3} onClick={resetFilters}>
            Reset Filters
          </Button>
        </FilterGroup>
      </FiltersWrapper>
      {error && (
        <WarningText>
          Error loading live trades. Try adjusting filters or refreshing the
          page.
        </WarningText>
      )}
      <TableContainer>
        <LiveTradesTable
          trades={filteredTrades}
          isLoading={isLoading}
          addressLabels={addressLabels}
        />
      </TableContainer>
    </LiveTradesWrapper>
  );
}
